import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  capitalize,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimatedNumber from "../../common/components/AnimatedNumber";
import { SPACING } from "../../common/constants/spacing";
import { getPlatformLogo } from "../../common/utils/platformUtils";

export const SourceAnalytics = ({ analytics }) => {
  const { t } = useTranslation("analytics");

  return (
    <Box>
      <Typography
        variant="h3"
        fontWeight="bold"
        sx={{ mt: SPACING.lg, mb: SPACING.sm }}
      >
        {t("sourceAnalytics.title")}
      </Typography>
      {!analytics?.source_analytics ||
      analytics.source_analytics.length === 0 ? (
        <Card sx={{ p: 3 }} variant="outlined">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <Typography variant="bodyMd" sx={{ color: "text.secondary" }}>
              {t("sourceAnalytics.noData")}
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card sx={{ p: 0 }} variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.source")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.platform")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.assets")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.impressions")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.impressionRevenue")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.sales")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.royaltyRevenue")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("sourceAnalytics.tableHeaders.totalRevenue")}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.source_analytics.map((source) => {
                  const totalRevenue =
                    source.analytics.total_impression_revenue +
                    source.analytics.total_royalty_revenue;

                  return (
                    <TableRow key={source.id} hover>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <img
                            src={getPlatformLogo(source.platform)}
                            alt={`${source.platform} Logo`}
                            style={{ height: "24px", objectFit: "contain" }}
                          />
                          <Typography variant="bodySm" fontWeight="medium">
                            {source.account_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={capitalize(source.platform_display)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="bodySm">
                          <AnimatedNumber
                            section="tables"
                            value={source.analytics.asset_count}
                          />
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="bodySm">
                          <AnimatedNumber
                            section="tables"
                            value={source.analytics.total_impressions}
                          />
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="bodySm" color="primary.main">
                          <AnimatedNumber
                            section="tables"
                            value={source.analytics.total_impression_revenue}
                            isCurrency
                          />
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography variant="bodySm">
                            <AnimatedNumber
                              section="tables"
                              value={source.analytics.total_sales_count}
                            />
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="bodySm" color="primary.main">
                          <AnimatedNumber
                            section="tables"
                            value={source.analytics.total_royalty_revenue}
                            isCurrency
                          />
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="bodySm"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          <AnimatedNumber
                            section="tables"
                            value={totalRevenue}
                            isCurrency
                          />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};
